import * as THREE from 'three';
import { patchMaterial } from '../atmosphere';
import type { TextureLibrary, TextureSet } from './textures';

// The ship's material library. Every material goes through patchMaterial()
// (atmosphere + cloud shadows) and most get extra shader patches: painted
// zones with wear-through, wetness from rain and spray, and cloth
// translucency for the sails.

/** Gunport rectangles (x0, x1, y0, y1) cut out of hull and bulwark. */
export const PORTS: THREE.Vector4[] = [];

export const MAX_HOLES = 24;
export const MAX_BREACHES = 8;
export const BURN_STATIONS = 12;

/** Per-ship shader inputs: weather on her timbers and the damage she's taken. */
export function makeShipUniforms() {
  return {
    /** Rain / spray wetness of exposed surfaces, 0..1. */
    uWet: { value: 0 },
    /** Water sloshing on deck, 0..1. */
    uDeckWater: { value: 0 },
    /** Night factor: lights in the stern windows. */
    uNightGlow: { value: 0 },
    /** Screen-space wire AA: 2*tan(fov/2)/viewportHeight. */
    uPixelAngle: { value: 0.001 },
    uTimeS: { value: 0 },
    /** Shot holes: design-space centre, radius (negative once plugged). */
    uHoles: { value: Array.from({ length: MAX_HOLES }, () => new THREE.Vector4(0, 0, 0, 0)) },
    /** Stove-in bulwarks: x, half width, depth below the rail, side. */
    uBreach: { value: Array.from({ length: MAX_BREACHES }, () => new THREE.Vector4(0, 0, 0, 0)) },
    /** Fire damage per station along the ship: char port, centre, starboard; live heat. */
    uBurn: { value: Array.from({ length: BURN_STATIONS }, () => new THREE.Vector4(0, 0, 0, 0)) },
  };
}
export type ShipUniforms = ReturnType<typeof makeShipUniforms>;

/** How a ship is painted and what colours she flies. */
export interface ShipLook {
  topside: THREE.Color;
  strake: THREE.Color;
  canvas: THREE.Color;
  flag: 'pirate' | 'navy' | 'merchant';
}

export const DEFAULT_LOOK: ShipLook = {
  topside: new THREE.Color(0.035, 0.033, 0.03), strake: new THREE.Color(0.36, 0.13, 0.06), canvas: new THREE.Color(1, 1, 1), flag: 'pirate',
};

function setRepeat(t: TextureSet): void {
  for (const tex of [t.map, t.normalMap, t.orm]) {
    tex.repeat.set(1 / t.size.x, 1 / t.size.y);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  }
}

function pbr(t: TextureSet, params: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    map: t.map,
    normalMap: t.normalMap,
    roughnessMap: t.orm,
    metalnessMap: t.orm,
    aoMap: t.orm,
    aoMapIntensity: 1,
    roughness: 1,
    metalness: 1,
    ...params,
  });
}

const LOCAL_VARYING_V = 'varying vec3 vLocalPos;\n';
const LOCAL_ASSIGN = `
#ifdef USE_INSTANCING
  vLocalPos = (instanceMatrix * vec4(position, 1.0)).xyz;
#else
  vLocalPos = position;
#endif
`;

const PAINT_GLSL = /* glsl */ `
varying vec3 vLocalPos;
uniform float uWet;
uniform float uDeckWater;
uniform vec3 uPaintA;
uniform vec3 uPaintB;
uniform float uZoneMode;
float pHash(vec2 p) { return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
float pNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(pHash(i), pHash(i + vec2(1, 0)), f.x), mix(pHash(i + vec2(0, 1)), pHash(i + vec2(1, 1)), f.x), f.y);
}
// Hull colour scheme by height: copper below the waterline, a tarred boot-top,
// black wales and topsides, a faded ochre-red gun strake.
vec3 hullPaint(vec3 p, float wearIn, out float wearOut, out float metal, out float roughAdj) {
  metal = 0.0;
  roughAdj = 0.0;
  wearOut = wearIn;
  float y = p.y;
  float gunStrake = smoothstep(2.25, 2.3, y) * (1.0 - smoothstep(3.02, 3.07, y)) * step(-4.8, p.x) * step(p.x, 11.5);
  vec3 black = uPaintB;
  vec3 ochre = uPaintA;
  vec3 c = mix(black, ochre, gunStrake);
  // Copper sheathing below the load line.
  if (y < -0.25 + sin(p.x * 0.7) * 0.03) {
    vec2 plate = vec2(p.x / 1.2 + floor(y / 0.36) * 0.5, y / 0.36);
    vec2 fp = fract(plate);
    float seam = 1.0 - smoothstep(0.0, 0.03, min(min(fp.x, 1.0 - fp.x) * 1.2, min(fp.y, 1.0 - fp.y) * 0.36));
    float verd = smoothstep(0.35, 0.75, pNoise(p.xy * vec2(0.9, 2.2)) * 0.6 + pNoise(p.xy * 6.0) * 0.4);
    float plateTone = pHash(floor(plate));
    vec3 copper = mix(vec3(0.55, 0.3, 0.18), vec3(0.42, 0.24, 0.15), plateTone);
    copper = mix(copper, vec3(0.18, 0.36, 0.3), verd * 0.8);
    copper = mix(copper, vec3(0.08, 0.1, 0.06), smoothstep(-1.2, -0.3, y) * 0.5);
    c = mix(copper, copper * 0.4, seam);
    metal = (1.0 - verd) * 0.75;
    roughAdj = -0.25 + verd * 0.3;
    wearOut = 0.0;
  } else if (y < 0.35) {
    c = vec3(0.02, 0.022, 0.018);
    roughAdj = -0.35;
    wearOut = wearIn * 0.3;
  }
  // Thin gilt moulding along the top of the strake.
  float gilt = (1.0 - smoothstep(0.02, 0.035, abs(y - 3.12))) * step(-4.8, p.x) * step(p.x, 11.5);
  c = mix(c, vec3(0.62, 0.46, 0.2), gilt);
  metal = max(metal, gilt * 0.8);
  return c;
}
`;

// Battle damage drawn straight into the hull shaders, in design space:
// jagged shot holes with splintered bare oak round them (or a plank patch once
// the carpenters have been at them), charring where fire has been, embers
// still glowing, and stove-in bulwarks cut clean out of the rail.
const DAMAGE_GLSL = /* glsl */ `
uniform vec4 uHoles[${MAX_HOLES}];
uniform vec4 uBreach[${MAX_BREACHES}];
uniform vec4 uBurn[${BURN_STATIONS}];
uniform float uTimeS;
float dSS(float a, float b, float x) { float t = clamp((x - a) / (b - a), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }
float designSheer(float x) { return 3.35 + 0.0021 * x * x + 2.0 * dSS(-3.5, -6.8, x) + 0.8 * dSS(9.0, 12.2, x); }
bool breached(vec3 p) {
  for (int i = 0; i < ${MAX_BREACHES}; i++) {
    vec4 b = uBreach[i];
    if (b.y <= 0.0 || p.z * b.w <= 0.0) continue;
    float jag = (pNoise(vec2(p.y * 7.0, b.x * 3.1)) - 0.5) * 0.35;
    float dy = designSheer(p.x) + 0.2 - p.y;
    float w = b.y * (1.0 - dSS(b.z * 0.4, b.z + 0.05, dy) * 0.6) + jag;
    if (abs(p.x - b.x) < w && dy < b.z + jag * 0.5) return true;
  }
  return false;
}
/** x: char (0..1), y: live heat. */
vec2 burnAt(vec3 p) {
  float f = clamp((p.x + 16.5) / 34.0 * ${BURN_STATIONS}.0 - 0.5, 0.0, ${BURN_STATIONS - 1}.0);
  int i0 = int(floor(f));
  int i1 = min(i0 + 1, ${BURN_STATIONS - 1});
  vec4 a = mix(uBurn[i0], uBurn[i1], fract(f));
  float lane = clamp(p.z / 2.6, -1.0, 1.0);
  float ch = lane < 0.0 ? mix(a.y, a.x, -lane) : mix(a.y, a.z, lane);
  return vec2(ch, a.w);
}
void shipDamage(vec3 p, inout vec3 col, inout float roughAdj, inout float metal, inout vec3 glow) {
  for (int i = 0; i < ${MAX_HOLES}; i++) {
    vec4 h = uHoles[i];
    if (h.w == 0.0 || p.z * h.z <= 0.0) continue;
    float r = abs(h.w);
    vec2 d = p.xy - h.xy;
    if (abs(d.x) > r * 2.2 || abs(d.y) > r * 2.2 || abs(abs(p.z) - abs(h.z)) > 0.8) continue;
    if (h.w < 0.0) {
      // Plugged: a patch of fresh planking nailed over the hole.
      vec2 q = abs(d);
      if (max(q.x, q.y * 1.3) < r * 1.3) {
        col = vec3(0.36, 0.25, 0.15) * (0.8 + 0.25 * pNoise(p.xy * vec2(1.5, 12.0)));
        roughAdj = 0.15;
        metal = 0.0;
      }
      continue;
    }
    float dist = length(d);
    float ang = atan(d.y, d.x);
    float fi = float(i);
    float jag = (pNoise(vec2(ang * 1.6 + fi * 7.0, fi)) - 0.5) * 0.6 + (pNoise(vec2(ang * 6.0, fi * 3.0)) - 0.5) * 0.35;
    float e = dist / r - jag;
    if (e < 1.0) {
      col = vec3(0.004, 0.0035, 0.003);
      roughAdj = 0.6;
      metal = 0.0;
    } else if (e < 1.9) {
      // Splintered bare oak, darker toward the edge of the paint.
      float s = pNoise(vec2(ang * 22.0, dist * 25.0));
      vec3 raw = mix(vec3(0.45, 0.32, 0.19), vec3(0.22, 0.15, 0.09), s);
      col = mix(raw, col, dSS(1.15, 1.9, e + s * 0.35));
      metal *= dSS(1.1, 1.9, e);
    }
  }
  vec2 b = burnAt(p);
  if (b.x > 0.01) {
    float n = pNoise(p.xy * 2.3 + p.z) * 0.6 + pNoise(p.xy * 7.1) * 0.4;
    float ch = dSS(0.25, 0.75, b.x * 1.3 - n * 0.6 + 0.2);
    col = mix(col, vec3(0.012, 0.01, 0.009), ch);
    roughAdj += ch * 0.3;
    metal *= 1.0 - ch;
    // Embers glowing in the cracks while it's still hot.
    float crack = dSS(0.55, 0.8, pNoise(p.xy * 9.0 + vec2(0.0, uTimeS * 0.3)));
    glow += vec3(1.0, 0.32, 0.06) * crack * ch * b.y * b.y * 6.0;
  }
}
`;

export interface ShipMaterials {
  hull: THREE.MeshStandardMaterial;
  bulwark: THREE.MeshStandardMaterial;
  deck: THREE.MeshStandardMaterial;
  timber: THREE.MeshStandardMaterial;
  spar: THREE.MeshStandardMaterial;
  blackWood: THREE.MeshStandardMaterial;
  redWood: THREE.MeshStandardMaterial;
  canvas: THREE.MeshStandardMaterial;
  rope: THREE.MeshStandardMaterial;
  ropeTarred: THREE.MeshStandardMaterial;
  iron: THREE.MeshStandardMaterial;
  brass: THREE.MeshStandardMaterial;
  gilt: THREE.MeshStandardMaterial;
  glass: THREE.MeshStandardMaterial;
  lampGlass: THREE.MeshStandardMaterial;
  dark: THREE.MeshStandardMaterial;
  leather: THREE.MeshStandardMaterial;
  tarp: THREE.MeshStandardMaterial;
  flag: THREE.MeshStandardMaterial;
  [key: string]: THREE.Material;
}

/**
 * Weathering baked by the art pipeline into a `grime` vertex attribute
 * (x ambient occlusion, y grime, z salt, w 1 - edge wear). Only the
 * Blender-built meshes carry it, so they get a variant of each material that
 * reads it; the procedural geometry keeps the plain one.
 */
const WEATHER_FRAG = /* glsl */ `
  {
    float gAo = clamp(vGrime.x * uWeatherK.w, 0.0, 1.0);
    float gDirt = clamp(vGrime.y * uWeatherK.x, 0.0, 1.0);
    float gSalt = clamp(vGrime.z * uWeatherK.y, 0.0, 1.0);
    float gWear = clamp((1.0 - vGrime.w) * uWeatherK.z, 0.0, 1.0) * uWear.a;
    float lum = dot(diffuseColor.rgb, vec3(0.3, 0.59, 0.11));
    // Dirt, soot and tar: darker, browner, a little flatter in colour.
    vec3 dirt = mix(vec3(lum), diffuseColor.rgb, 0.6) * vec3(0.6, 0.54, 0.46);
    diffuseColor.rgb = mix(diffuseColor.rgb, dirt, gDirt * 0.75);
    // Edges and walkways rubbed back to what's underneath.
    diffuseColor.rgb = mix(diffuseColor.rgb, max(diffuseColor.rgb, uWear.rgb), gWear);
    // Dried spray.
    diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.6, 0.59, 0.55), gSalt * 0.5);
    roughnessFactor = clamp(roughnessFactor + gDirt * 0.08 + gSalt * 0.25 - gWear * 0.2, 0.04, 1.0);
    // Crevices see less of the sun as well as the sky.
    diffuseColor.rgb *= 1.0 - gAo * 0.3;
  }
`;
const WEATHER_AO = /* glsl */ `
  {
    float occ = 1.0 - clamp(vGrime.x * uWeatherK.w, 0.0, 1.0) * 0.75;
    reflectedLight.indirectDiffuse *= occ;
    reflectedLight.indirectSpecular *= mix(1.0, occ, 0.8);
  }
`;
/** What worn edges show (rgb) and how much wear shows at all (a), per material. */
const WEAR: Record<string, [number, number, number, number]> = {
  hull: [0.12, 0.1, 0.08, 0.5],
  bulwark: [0.2, 0.15, 0.11, 0.5],
  blackWood: [0.14, 0.12, 0.09, 0.6],
  redWood: [0.26, 0.17, 0.12, 0.5],
  timber: [0.46, 0.4, 0.31, 0.5],
  spar: [0.5, 0.42, 0.3, 0.4],
  deck: [0.62, 0.57, 0.48, 0.45],
  iron: [0.3, 0.3, 0.31, 0.6],
  brass: [0.95, 0.75, 0.4, 0.5],
  gilt: [0.95, 0.78, 0.42, 0.4],
  leather: [0.5, 0.33, 0.2, 0.5],
  tarp: [0.4, 0.37, 0.32, 0.4],
};
/** Overall strength of the bake: x grime, y salt, z wear, w occlusion. */
export const weatherTuning = { value: new THREE.Vector4(1, 1, 1, 1) };
const weatheredCache = new WeakMap<THREE.Material, THREE.Material>();

/** The variant of a ship material that reads the baked `grime` attribute. */
export function weathered(m: THREE.Material, key: string): THREE.Material {
  const hit = weatheredCache.get(m);
  if (hit) return hit;
  if (!(m as THREE.MeshStandardMaterial).isMeshStandardMaterial || key === 'canvas' || key === 'flag' || key === 'glass' || key === 'lampGlass') {
    weatheredCache.set(m, m);
    return m;
  }
  const w = (m as THREE.MeshStandardMaterial).clone();
  const wear = new THREE.Vector4(...(WEAR[key] ?? [0.4, 0.36, 0.3, 0.0]));
  w.onBeforeCompile = (shader, renderer) => {
    m.onBeforeCompile(shader, renderer);
    shader.uniforms.uWear = { value: wear };
    shader.uniforms.uWeatherK = weatherTuning;
    shader.vertexShader = 'attribute vec4 grime;\nvarying vec4 vGrime;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n vGrime = grime;');
    shader.fragmentShader = 'varying vec4 vGrime;\nuniform vec4 uWear;\nuniform vec4 uWeatherK;\n' + shader.fragmentShader
      .replace('#include <lights_physical_fragment>', WEATHER_FRAG + '#include <lights_physical_fragment>')
      .replace('#include <aomap_fragment>', '#include <aomap_fragment>' + WEATHER_AO);
  };
  w.customProgramCacheKey = () => m.customProgramCacheKey() + '+weather';
  weatheredCache.set(m, w);
  return w;
}

const instancedCache = new WeakMap<THREE.Material, THREE.Material>();

/**
 * The material to draw instanced meshes with: a twin of `m` used only by
 * instanced meshes. Three.js keeps a material's program for either
 * instanced or plain meshes, so one material shared by both is switched
 * between the two program variants, and the variant looked up again, at
 * every draw that changes kind. The twin shares the shader patches and all
 * the uniforms, so it looks exactly the same.
 */
export function forInstancing(m: THREE.Material): THREE.Material {
  let t = instancedCache.get(m);
  if (!t) {
    t = m.clone();
    t.onBeforeCompile = m.onBeforeCompile;
    t.customProgramCacheKey = m.customProgramCacheKey;
    t.name = m.name;
    instancedCache.set(m, t);
  }
  return t;
}

/** A material with its weathered variant and instanced twins (to dispose of them together). */
export function materialFamily(m: THREE.Material): THREE.Material[] {
  const out = new Set<THREE.Material>([m]);
  const w = weatheredCache.get(m);
  if (w) out.add(w);
  for (const x of [...out]) {
    const t = instancedCache.get(x);
    if (t) out.add(t);
  }
  return [...out];
}

/** Wetness: darker albedo, glossier surface. */
const WET_FRAG = /* glsl */ `
  float wetAmt = clamp(uWet, 0.0, 1.0);
`;

export function createShipMaterials(tex: TextureLibrary, shipUniforms: ShipUniforms = makeShipUniforms(), look: ShipLook = DEFAULT_LOOK): ShipMaterials {
  for (const t of [tex.hull, tex.deck, tex.timber, tex.canvas, tex.rope, tex.iron]) setRepeat(t);

  const paintPatch = (mode: 'hull' | 'solid' | 'none', paintA: THREE.Color, paintB: THREE.Color, ports = false) => (shader: THREE.WebGLProgramParametersWithUniforms) => {
    Object.assign(shader.uniforms, shipUniforms, {
      uPaintA: { value: paintA },
      uPaintB: { value: paintB },
      uZoneMode: { value: 0 },
      uPorts: { value: PORTS },
    });
    shader.vertexShader = LOCAL_VARYING_V + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n' + LOCAL_ASSIGN);
    let frag = PAINT_GLSL + (ports ? DAMAGE_GLSL : '') + shader.fragmentShader;
    let paint = '';
    if (mode === 'hull') {
      paint = `
      {
        float wearOut; float metalZ; float roughZ;
        vec3 pc = hullPaint(vLocalPos, sampledDiffuseColor.a, wearOut, metalZ, roughZ);
        diffuseColor.rgb = mix(pc * (0.85 + 0.3 * pNoise(vLocalPos.xy * 3.0)), diffuseColor.rgb, wearOut);
        diffuseColor.a = 1.0;
        vHullMetal = metalZ;
        vHullRough = roughZ;
      }`;
    } else if (mode === 'solid') {
      paint = `
      {
        float w = sampledDiffuseColor.a;
        diffuseColor.rgb = mix(uPaintA * (0.85 + 0.3 * pNoise(vLocalPos.xz * 2.0 + vLocalPos.y)), diffuseColor.rgb, w * 0.85);
        diffuseColor.a = 1.0;
      }`;
    } else {
      paint = '\n diffuseColor.a = 1.0;';
    }
    const portCut = ports ? `
      for (int i = 0; i < 16; i++) {
        vec4 pr = uPorts[i];
        if (vLocalPos.x > pr.x && vLocalPos.x < pr.y && vLocalPos.y > pr.z && vLocalPos.y < pr.w && abs(vLocalPos.z) > 1.0) discard;
      }
      if (breached(vLocalPos)) discard;` : '';
    const damage = ports ? `
      shipDamage(vLocalPos, diffuseColor.rgb, vHullRough, vHullMetal, vDamageGlow);` : '';
    frag = frag
      .replace('void main() {', 'uniform vec4 uPorts[16];\nfloat vHullMetal = 0.0; float vHullRough = 0.0; vec3 vDamageGlow = vec3(0.0);\nvoid main() {' + portCut)
      .replace('#include <map_fragment>', '#include <map_fragment>' + paint + damage)
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n totalEmissiveRadiance += vDamageGlow;')
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
        ${WET_FRAG}
        // Hull below the load line and splashed topsides are wet.
        float spray = (1.0 - smoothstep(0.2, 2.2, vLocalPos.y)) * 0.8;
        float wetHere = max(wetAmt, spray * ${mode === 'hull' ? '1.0' : '0.0'});
        roughnessFactor = clamp(roughnessFactor + vHullRough, 0.05, 1.0);
        roughnessFactor = mix(roughnessFactor, 0.12, wetHere * 0.85);
        diffuseColor.rgb *= mix(1.0, 0.58, wetHere * (1.0 - vHullMetal));`,
      )
      .replace('#include <metalnessmap_fragment>', '#include <metalnessmap_fragment>\n metalnessFactor = max(metalnessFactor, vHullMetal);');
    shader.fragmentShader = frag;
  };

  const hull = patchMaterial(pbr(tex.hull), paintPatch('hull', look.strake.clone(), look.topside.clone(), true), 'hull');
  const bulwark = patchMaterial(pbr(tex.hull), paintPatch('solid', look.strake.clone().multiplyScalar(0.85), look.topside.clone(), true), 'bulwark');

  // Deck: puddles form when water comes aboard or it rains.
  const deck = patchMaterial(pbr(tex.deck), (shader) => {
    Object.assign(shader.uniforms, shipUniforms);
    shader.vertexShader = LOCAL_VARYING_V + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n' + LOCAL_ASSIGN);
    shader.fragmentShader = PAINT_GLSL + DAMAGE_GLSL + shader.fragmentShader.replace(
      'void main() {', 'vec3 vDamageGlow = vec3(0.0);\nvoid main() {',
    ).replace(
      '#include <map_fragment>',
      `#include <map_fragment>
      {
        vec2 b = burnAt(vLocalPos);
        if (b.x > 0.01) {
          float n = pNoise(vLocalPos.xz * 2.1) * 0.6 + pNoise(vLocalPos.xz * 6.3) * 0.4;
          float ch = dSS(0.25, 0.75, b.x * 1.3 - n * 0.6 + 0.2);
          diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.012, 0.01, 0.009), ch);
          float crack = dSS(0.55, 0.8, pNoise(vLocalPos.xz * 9.0 + vec2(uTimeS * 0.3)));
          vDamageGlow = vec3(1.0, 0.32, 0.06) * crack * ch * b.y * b.y * 6.0;
        }
      }`,
    ).replace(
      '#include <emissivemap_fragment>',
      '#include <emissivemap_fragment>\n totalEmissiveRadiance += vDamageGlow;',
    ).replace(
      '#include <roughnessmap_fragment>',
      `#include <roughnessmap_fragment>
      float puddle = smoothstep(0.45, 0.75, pNoise(vLocalPos.xz * 0.9) * 0.7 + pNoise(vLocalPos.xz * 3.1) * 0.3);
      float wetD = clamp(max(uWet, uDeckWater), 0.0, 1.0);
      float pool = wetD * puddle;
      roughnessFactor = mix(roughnessFactor, 0.18, wetD * 0.8);
      roughnessFactor = mix(roughnessFactor, 0.03, pool);
      diffuseColor.rgb *= mix(1.0, 0.55, wetD);`,
    );
  }, 'deck');

  const wetM = (map: TextureSet, color: THREE.Color, key: string, rough = 1) =>
    patchMaterial(pbr(map, { color, roughness: rough }), (shader) => {
      Object.assign(shader.uniforms, shipUniforms);
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
        roughnessFactor = mix(roughnessFactor, 0.15, uWet * 0.8);
        diffuseColor.rgb *= mix(1.0, 0.6, uWet);
        diffuseColor.a = 1.0;`,
      ).replace('uniform vec3 diffuse;', 'uniform vec3 diffuse;\nuniform float uWet;');
    }, key);
  const timberM = (color: THREE.Color, key: string, rough = 1) => wetM(tex.timber, color, key, rough);

  const timber = timberM(new THREE.Color(1, 1, 1), 'timber');
  const spar = timberM(new THREE.Color(1.15, 1.05, 0.9), 'spar');
  const blackWood = patchMaterial(pbr(tex.timber), paintPatch('solid', new THREE.Color(0.03, 0.028, 0.026), new THREE.Color()), 'solid');
  const redWood = patchMaterial(pbr(tex.timber), paintPatch('solid', new THREE.Color(0.32, 0.055, 0.035), new THREE.Color()), 'solid');

  // Sails: double sided, light shines through backlit canvas.
  const canvas = patchMaterial(pbr(tex.canvas, { side: THREE.DoubleSide, metalness: 0, color: look.canvas.clone() }), (shader) => {
    Object.assign(shader.uniforms, shipUniforms);
    shader.fragmentShader = shader.fragmentShader
      .replace('uniform vec3 diffuse;', 'uniform vec3 diffuse;\nuniform float uWet;')
      .replace(
        '#include <lights_fragment_end>',
        `#include <lights_fragment_end>
        {
          // Translucency: sunlight arriving on the far side of the cloth.
          vec3 Lw = normalize(uSunDir);
          vec3 nW = normalize((vec4(normal, 0.0) * viewMatrix).xyz);
          float back = max(0.0, -dot(nW, Lw));
          float trans = back * 0.32 * (1.0 - uWet * 0.3);
          reflectedLight.indirectDiffuse += diffuseColor.rgb * uSunColor * trans * atmoCloudShadow(cameraPosition + vAtmoRay) / PI;
        }`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
        diffuseColor.rgb *= mix(1.0, 0.72, uWet);
        diffuseColor.a = 1.0;`,
      );
  }, 'canvas');

  // Rope with sub-pixel wire anti-aliasing: thin lines keep a minimum
  // on-screen width and fade out with alpha-to-coverage instead of aliasing.
  const ropeM = (color: THREE.Color, key: string) =>
    patchMaterial(pbr(tex.rope, { color, metalness: 0, alphaToCoverage: true, transparent: false }), (shader) => {
      Object.assign(shader.uniforms, shipUniforms);
      shader.vertexShader = 'uniform float uPixelAngle;\nvarying float vCover;\n' + shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        {
          #ifdef USE_INSTANCING
            mat4 im = instanceMatrix;
          #else
            mat4 im = mat4(1.0);
          #endif
          float rWorld = length((modelMatrix * im * vec4(1.0, 0.0, 0.0, 0.0)).xyz);
          vec4 axisView = modelViewMatrix * im * vec4(0.0, position.y, 0.0, 1.0);
          float pixR = -axisView.z * uPixelAngle * 0.6;
          float grow = max(1.0, pixR / max(rWorld, 1e-5));
          transformed.xz *= grow;
          vCover = 1.0 / grow;
        }`,
      ).replace(
        '#include <uv_vertex>',
        `#include <uv_vertex>
        {
          #ifdef USE_INSTANCING
            float ropeR = length((instanceMatrix * vec4(1.0, 0.0, 0.0, 0.0)).xyz);
            float ropeL = length((instanceMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz);
          #else
            float ropeR = 0.03; float ropeL = 1.0;
          #endif
          vec2 rs = vec2(ropeR * 6.2832 / 0.1, ropeL / 0.4) / vec2(10.0, 2.5);
          #ifdef USE_MAP
            vMapUv *= rs;
          #endif
          #ifdef USE_NORMALMAP
            vNormalMapUv *= rs;
          #endif
          #ifdef USE_ROUGHNESSMAP
            vRoughnessMapUv *= rs;
          #endif
          #ifdef USE_METALNESSMAP
            vMetalnessMapUv *= rs;
          #endif
          #ifdef USE_AOMAP
            vAoMapUv *= rs;
          #endif
        }`,
      );
      shader.fragmentShader = 'varying float vCover;\n' + shader.fragmentShader.replace(
        '#include <opaque_fragment>',
        '#include <opaque_fragment>\n gl_FragColor.a = clamp(vCover * 1.15, 0.0, 1.0);',
      );
    }, key);
  const rope = ropeM(new THREE.Color(1.0, 0.95, 0.85), 'rope');
  const ropeTarred = ropeM(new THREE.Color(0.32, 0.27, 0.22), 'ropeT');

  const iron = patchMaterial(pbr(tex.iron), undefined, 'iron');
  const brass = patchMaterial(new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.62, 0.44, 0.2), metalness: 1, roughness: 0.38, normalMap: tex.iron.normalMap, normalScale: new THREE.Vector2(0.3, 0.3),
  }), undefined, 'brass');
  const gilt = patchMaterial(new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.7, 0.52, 0.22), metalness: 1, roughness: 0.42, normalMap: tex.timber.normalMap,
  }), undefined, 'gilt');
  const glass = patchMaterial(new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.02, 0.025, 0.03), metalness: 0, roughness: 0.06, emissive: new THREE.Color(1.0, 0.55, 0.22), emissiveIntensity: 0,
  }), (shader) => {
    Object.assign(shader.uniforms, shipUniforms);
    shader.fragmentShader = 'uniform float uNightGlow;\n' + shader.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      '#include <emissivemap_fragment>\n totalEmissiveRadiance = emissive * uNightGlow * 0.9;',
    );
  }, 'glass');
  const lampGlass = patchMaterial(new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.25, 0.2, 0.12), metalness: 0, roughness: 0.2, emissive: new THREE.Color(1.0, 0.62, 0.28), emissiveIntensity: 1,
    transparent: false,
  }), (shader) => {
    Object.assign(shader.uniforms, shipUniforms);
    shader.fragmentShader = 'uniform float uNightGlow;\nuniform float uTimeS;\n' + shader.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      '#include <emissivemap_fragment>\n totalEmissiveRadiance = emissive * (0.03 + uNightGlow * 1.6) * (0.9 + 0.1 * sin(uTimeS * 17.0 + vAtmoRay.x));',
    );
  }, 'lamp');
  const dark = patchMaterial(new THREE.MeshStandardMaterial({ color: 0x050403, roughness: 1, metalness: 0 }), undefined, 'dark');
  const flag = patchMaterial(new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, roughness: 0.95, metalness: 0, map: flagTexture(look.flag) }), undefined, 'flag');

  // Leather (fire buckets): dark tanned hide over the timber grain.
  const leather = timberM(new THREE.Color(0.42, 0.22, 0.12), 'leather', 0.7);
  // Tarred tarpaulin (over the hammock nettings, hatch covers).
  const tarp = wetM(tex.canvas, new THREE.Color(0.26, 0.24, 0.2), 'tarp', 0.8);
  return { hull, bulwark, deck, timber, spar, blackWood, redWood, canvas, rope, ropeTarred, iron, brass, gilt, glass, lampGlass, dark, flag, leather, tarp };
}

const flagCache = new Map<string, THREE.Texture>();

function flagTexture(kind: ShipLook['flag']): THREE.Texture {
  let t = flagCache.get(kind);
  if (!t) flagCache.set(kind, (t = kind === 'pirate' ? makeJollyRoger() : makeEnsign(kind === 'navy')));
  return t;
}

/** A naval or merchant ensign: white or red field, crosses in the canton. */
function makeEnsign(navy: boolean): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 300;
  const g = c.getContext('2d')!;
  g.fillStyle = navy ? '#e8e4da' : '#9c1c16';
  g.fillRect(0, 0, 512, 300);
  if (navy) {
    g.fillStyle = '#b3201a';
    g.fillRect(0, 128, 512, 44);
    g.fillRect(234, 0, 44, 300);
  }
  // Canton: a blue field with crosses.
  g.fillStyle = '#1d2a55';
  g.fillRect(0, 0, 230, 140);
  g.strokeStyle = '#e8e4da';
  g.lineWidth = 22;
  g.beginPath();
  g.moveTo(0, 0);
  g.lineTo(230, 140);
  g.moveTo(230, 0);
  g.lineTo(0, 140);
  g.stroke();
  g.strokeStyle = '#b3201a';
  g.lineWidth = 8;
  g.stroke();
  g.fillStyle = '#e8e4da';
  g.fillRect(0, 52, 230, 36);
  g.fillRect(97, 0, 36, 140);
  g.fillStyle = '#b3201a';
  g.fillRect(0, 59, 230, 22);
  g.fillRect(104, 0, 22, 140);
  // Weathering.
  for (let i = 0; i < 300; i++) {
    g.fillStyle = `rgba(40,30,20,${Math.random() * 0.06})`;
    g.beginPath();
    g.arc(Math.random() * 512, Math.random() * 300, 4 + Math.random() * 26, 0, Math.PI * 2);
    g.fill();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/** Plain, untextured materials for ships far off. */
export interface LowShipMaterials {
  hull: THREE.MeshStandardMaterial;
  deck: THREE.MeshStandardMaterial;
  spar: THREE.MeshStandardMaterial;
  canvas: THREE.MeshStandardMaterial;
  flag: THREE.MeshStandardMaterial;
  pennant: THREE.MeshStandardMaterial;
  [key: string]: THREE.Material;
}

const lowCache = new Map<string, LowShipMaterials>();

/**
 * Flat colours instead of textures, no damage shading, shared by every
 * distant ship with the same paint. The hull keeps its colour scheme by
 * height (copper, boot-top, topsides, gun strake) so she still reads as
 * herself on the horizon.
 */
export function createLowMaterials(look: ShipLook): LowShipMaterials {
  const key = `${look.topside.getHexString()}-${look.strake.getHexString()}-${look.canvas.getHexString()}-${look.flag}`;
  const hit = lowCache.get(key);
  if (hit) return hit;
  // No ambient-occlusion maps here, so sky reflections are turned down to
  // match the textured versions' overall tone.
  const flat = (color: THREE.Color, roughness: number, side: THREE.Side = THREE.FrontSide) =>
    patchMaterial(new THREE.MeshStandardMaterial({ color, roughness, metalness: 0, side, envMapIntensity: 0.6 }), undefined, 'low');
  const hull = patchMaterial(new THREE.MeshStandardMaterial({ color: look.topside.clone(), roughness: 0.92, metalness: 0, side: THREE.DoubleSide, envMapIntensity: 0.45 }), (shader) => {
    shader.uniforms.uStrake = { value: look.strake.clone() };
    shader.vertexShader = LOCAL_VARYING_V + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n' + LOCAL_ASSIGN);
    shader.fragmentShader = LOCAL_VARYING_V + 'uniform vec3 uStrake;\n' + shader.fragmentShader.replace(
      '#include <color_fragment>',
      `#include <color_fragment>
      {
        vec3 p = vLocalPos;
        float band = step(2.25, p.y) * step(p.y, 3.07) * step(-4.8, p.x) * step(p.x, 11.5);
        diffuseColor.rgb = mix(diffuseColor.rgb, uStrake, band);
        if (p.y < -0.25) diffuseColor.rgb = vec3(0.4, 0.25, 0.16);
        else if (p.y < 0.35) diffuseColor.rgb = vec3(0.02, 0.022, 0.018);
      }`,
    );
  }, 'low-hull');
  const flagColor = look.flag === 'pirate' ? new THREE.Color(0.012, 0.011, 0.01) : look.flag === 'navy' ? new THREE.Color(0.72, 0.68, 0.62) : new THREE.Color(0.38, 0.03, 0.02);
  const low: LowShipMaterials = {
    hull,
    deck: flat(new THREE.Color(0.22, 0.17, 0.11), 0.9),
    spar: flat(new THREE.Color(0.24, 0.15, 0.08), 0.8),
    canvas: flat(look.canvas.clone().multiply(new THREE.Color(0.86, 0.81, 0.71)), 0.9, THREE.DoubleSide),
    flag: flat(flagColor, 0.95, THREE.DoubleSide),
    pennant: flat(new THREE.Color(0.35, 0.03, 0.02), 0.95, THREE.DoubleSide),
  };
  lowCache.set(key, low);
  return low;
}

/** The black flag: skull and crossed bones, drawn procedurally. */
function makeJollyRoger(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 340;
  const g = c.getContext('2d')!;
  g.fillStyle = '#0b0a0a';
  g.fillRect(0, 0, 512, 340);
  // Weathering blotches.
  for (let i = 0; i < 400; i++) {
    g.fillStyle = `rgba(${40 + Math.random() * 20},${36 + Math.random() * 15},${32},${Math.random() * 0.08})`;
    g.beginPath();
    g.arc(Math.random() * 512, Math.random() * 340, 4 + Math.random() * 30, 0, Math.PI * 2);
    g.fill();
  }
  const bone = '#d9d2bd';
  g.fillStyle = bone;
  g.strokeStyle = bone;
  // Crossed bones.
  g.lineCap = 'round';
  g.lineWidth = 22;
  for (const [x0, y0, x1, y1] of [[170, 250, 342, 318], [342, 250, 170, 318]] as const) {
    g.beginPath();
    g.moveTo(x0, y0);
    g.lineTo(x1, y1);
    g.stroke();
    for (const [x, y] of [[x0, y0], [x1, y1]]) {
      g.beginPath();
      g.arc(x! - 8, y! - 6, 14, 0, Math.PI * 2);
      g.arc(x! + 8, y! + 6, 14, 0, Math.PI * 2);
      g.fill();
    }
  }
  // Skull.
  g.beginPath();
  g.ellipse(256, 150, 78, 86, 0, 0, Math.PI * 2);
  g.fill();
  g.fillRect(212, 190, 88, 62);
  g.fillStyle = '#0b0a0a';
  g.beginPath();
  g.ellipse(226, 152, 22, 26, 0.2, 0, Math.PI * 2);
  g.ellipse(286, 152, 22, 26, -0.2, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.moveTo(256, 178);
  g.lineTo(244, 204);
  g.lineTo(268, 204);
  g.closePath();
  g.fill();
  for (let i = 0; i < 6; i++) g.fillRect(218 + i * 13, 226, 4, 26);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}
